import type {
  GridOption,
  GridSearchBarOption,
  MenuItem,
  ContextMenuItems,
} from '@/common/utils/types';
import type { BasePaginationProps } from '@/common/components/molecules/basePagination/basePagination.setup';
import type {
  GridOptionV2,
  PaginationOption,
  TextFilterOption,
  TokenFilterOption,
  GridSortingOption,
  GridRowSelectionOption,
  GridStyleOption,
  GridDetailRowOption,
  GridSettingOption,
  GridColumnAutoFitOption,
  GridColumnVisibilityOption,
  GridColumnDef,
  GridRawRowDataType,
  GridRowDataType,
  GridRowMenuOption,
  GridExpandingOption,
  GridSummaryOption,
} from '@/common/components/molecules/modernGrid/modernGrid.types';
import type { ExportExcelOption } from '@/common/utils/exportExcelUtils';
import { getTrendChartLastNumericValue } from '@/common/components/molecules/trendChartCell/trendChartCell.util';
import type { TrendChartTickInfo } from '@/common/components/molecules/trendChartCell/tendChartCell.type';
import type { Row, SortingFn } from '@tanstack/vue-table';

export interface PaginationInfo extends BasePaginationProps {
  use?: boolean;
  useClient?: boolean;
}

export interface BaseGridAdapterOptions {
  isTreeMode?: boolean;
}

const BORDER_STYLE_MAP: Record<string, { showRowLine: boolean; showColumnLine: boolean }> = {
  all: { showRowLine: true, showColumnLine: true },
  rows: { showRowLine: true, showColumnLine: false },
  columns: { showRowLine: false, showColumnLine: true },
  none: { showRowLine: false, showColumnLine: false },
};

const isArrayEmpty = (v: unknown): boolean =>
  Array.isArray(v) && (v.length === 0 || v.every((item) => item == null || item === ''));

/**
 * trend-chart-cell 셀({ times, values })은 최근값이 없으면 빈 값으로 취급해 정렬 시 항상 하단에 둔다.
 * 빈 값 기준을 sortTrendChartCellAsc와 하나로 유지하기 위해 같은 최근값 헬퍼를 재사용한다
 */
export const isTrendChartCellEmpty = (v: unknown): boolean =>
  typeof v === 'object' &&
  v !== null &&
  'times' in v &&
  'values' in v &&
  getTrendChartLastNumericValue(v as TrendChartTickInfo) === null;

const wrapCustomAscFunction = <T extends GridRawRowDataType>(
  customFn: (a: unknown, b: unknown) => number,
  columnId: string,
): SortingFn<GridRowDataType<T>> => {
  return (rowA: Row<GridRowDataType<T>>, rowB: Row<GridRowDataType<T>>): number => {
    const a = rowA.original[columnId];
    const b = rowB.original[columnId];

    const isEmptyA = a == null || a === '' || isArrayEmpty(a) || isTrendChartCellEmpty(a);
    const isEmptyB = b == null || b === '' || isArrayEmpty(b) || isTrendChartCellEmpty(b);

    if (isEmptyA && isEmptyB) return 0;
    if (isEmptyA || isEmptyB) {
      // TanStack은 desc일 때 결과를 *= -1 하므로, 방향을 알아야 빈 값을 항상 하단에 유지할 수 있음
      const table = rowA.getAllCells()[0]?.getContext().table;
      const isDesc = table?.getState().sorting.some((s) => s.id === columnId && s.desc) ?? false;
      if (isEmptyA) return isDesc ? -1 : 1;
      if (isEmptyB) return isDesc ? 1 : -1;
    }

    return customFn(a, b);
  };
};

export const applyCustomAscFunction = <T extends GridRawRowDataType>(
  columns: GridColumnDef<T>[],
  customAscFunction?: Record<string, (a: unknown, b: unknown) => number>,
): GridColumnDef<T>[] => {
  if (!customAscFunction || Object.keys(customAscFunction).length === 0) return columns;

  return columns.map((column) => {
    const columnId = column.accessorKey || column.id;
    if (columnId && customAscFunction[columnId]) {
      return {
        ...column,
        sortingFn: wrapCustomAscFunction<T>(customAscFunction[columnId], columnId),
      } as GridColumnDef<T>;
    }
    return column;
  });
};

const transformStyleOption = (
  gridOption?: GridOption,
): Pick<GridStyleOption, 'body' | 'showBorder'> | undefined => {
  if (!gridOption) return undefined;

  const body: GridStyleOption['body'] = {};
  let showBorder: boolean | undefined;

  if (gridOption.rowHeight !== undefined) body.rowHeight = gridOption.rowHeight;
  if (gridOption.rowMinHeight !== undefined) body.rowMinHeight = gridOption.rowMinHeight;

  if (gridOption.style) {
    if (gridOption.style.stripe !== undefined) body.useStripe = gridOption.style.stripe;

    const borderStyle = gridOption.style.border;
    if (borderStyle && BORDER_STYLE_MAP[borderStyle]) {
      Object.assign(body, BORDER_STYLE_MAP[borderStyle]);
    }
    if (borderStyle === 'none') showBorder = false;
  }

  const hasBody = Object.keys(body).length > 0;
  if (!hasBody && showBorder === undefined) return undefined;

  return {
    ...(hasBody && { body }),
    ...(showBorder !== undefined && { showBorder }),
  };
};

const transformRowSelectionOption = (
  gridOption?: GridOption,
): GridRowSelectionOption | undefined => {
  if (!gridOption?.useCheckbox && !gridOption?.useSelection) return undefined;

  const result: GridRowSelectionOption = {};

  if (gridOption.useCheckbox) {
    const { use = false, headerCheck = true, mode = 'multi' } = gridOption.useCheckbox;
    result.useCheckbox = use;
    result.useCheckAll = headerCheck;
    result.mode = mode;
    result.useMultiCheck = mode !== 'single';
    if (mode === 'each') result.enableSubRowCheck = false;
  }

  if (gridOption.useSelection) {
    const {
      use = true,
      multiple,
      limitCount,
      disableDeselect,
      disableRootNodeSelect,
      disableCellClickSelect,
    } = gridOption.useSelection;
    result.enableSelect = use;
    if (multiple !== undefined) result.multiple = multiple;
    if (limitCount !== undefined) result.limitCount = limitCount;
    if (disableDeselect !== undefined) result.disableDeselect = disableDeselect;
    if (disableRootNodeSelect !== undefined) result.disableRootNodeSelect = disableRootNodeSelect;
    if (disableCellClickSelect !== undefined)
      result.disableCellClickSelect = disableCellClickSelect;
  }

  return result;
};

const transformPaginationOption = (
  gridOption?: GridOption,
  paginationInfo?: PaginationInfo,
): PaginationOption | undefined => {
  if (paginationInfo !== undefined) {
    if (!paginationInfo.use) return undefined;
    return {
      use: true,
      useClient: paginationInfo.useClient ?? false,
      current: paginationInfo.current ?? 1,
      total: paginationInfo.total ?? 0,
      perData: paginationInfo.perData ?? 20,
      items: paginationInfo.items ?? [20, 30, 40, 50],
      align: paginationInfo.align ?? 'right',
      showSetting: paginationInfo.showSetting ?? false,
      showPageInfo: paginationInfo.showPageInfo ?? false,
    };
  }

  if (!gridOption?.page?.use) return undefined;

  return {
    use: true,
    useClient: gridOption.page.useClient ?? false,
    current: gridOption.page.currentPage ?? 1,
    total: gridOption.page.total ?? 0,
    perData: gridOption.page.perPage ?? 20,
    showPageInfo: gridOption.page.showPageInfo ?? false,
  };
};

const transformTextFilterOption = (
  searchBarOption?: GridSearchBarOption & { useExternalSearchBar?: boolean },
): TextFilterOption | undefined => {
  if (!searchBarOption?.use || searchBarOption.filterSearch?.use) return undefined;

  if (searchBarOption.useExternalSearchBar) {
    return { use: true, useClient: true, hideInput: true };
  }

  return {
    use: true,
    useClient: searchBarOption.useClientFilter ?? false,
    mode: searchBarOption.mode ?? 'full',
    placeholder: searchBarOption.placeholder ?? '',
  };
};

const transformTokenFilterOption = (
  searchBarOption?: GridSearchBarOption & { useExternalSearchBar?: boolean },
): TokenFilterOption | undefined => {
  if (!searchBarOption?.use || !searchBarOption.filterSearch?.use) return undefined;
  if (searchBarOption.useExternalSearchBar) return undefined;

  return {
    use: true,
    useClient: true,
    mode: searchBarOption.mode ?? 'full',
    storageKey: searchBarOption.filterSearch.storageKey ?? '',
    filterItems: searchBarOption.filterSearch.filterItems ?? [],
    customOption: searchBarOption.filterSearch.customOption,
    useAutoValueItems: searchBarOption.filterSearch.useAutoValueItems,
    defaultActive: searchBarOption.filterSearch.defaultActive,
    persistActiveState: searchBarOption.filterSearch.persistActiveState,
  };
};

const transformDetailRowOption = (gridOption?: GridOption): GridDetailRowOption | undefined => {
  if (!gridOption?.rowDetail?.use) return undefined;
  return {
    use: true,
    detailRowKey: 'detail',
    detailRowHeight: gridOption.rowDetail.detailRowHeight,
  };
};

const transformSortingOption = (gridOption?: GridOption): GridSortingOption | undefined => {
  const hasUseSort = gridOption?.useSort !== undefined;
  const hasHiddenMenuItem = gridOption?.hiddenColumnMenuItem !== undefined;
  const hasColumnMenuText = gridOption?.columnMenuText !== undefined;

  if (!hasUseSort && !hasHiddenMenuItem && !hasColumnMenuText) return undefined;

  let useSort = true;
  if (gridOption?.useSort !== undefined) {
    useSort =
      typeof gridOption.useSort === 'boolean'
        ? gridOption.useSort
        : (gridOption.useSort?.value ?? true);
  }

  const result: GridSortingOption = { enable: useSort, multi: true };

  if (hasHiddenMenuItem || hasColumnMenuText) {
    const hidden = gridOption?.hiddenColumnMenuItem ?? {};
    const text = gridOption?.columnMenuText ?? {};
    result.columnMenu = {};

    if (hidden.ascending !== undefined || text.ascending !== undefined) {
      result.columnMenu.ascending = { use: hidden.ascending !== true, title: text.ascending };
    }
    if (hidden.descending !== undefined || text.descending !== undefined) {
      result.columnMenu.descending = { use: hidden.descending !== true, title: text.descending };
    }
  }

  return result;
};

const transformColumnVisibilityOption = (
  gridOption?: GridOption,
): GridColumnVisibilityOption | undefined => {
  const hidden = gridOption?.hiddenColumnMenuItem;
  const text = gridOption?.columnMenuText;

  if (hidden?.hide === undefined && text?.hide === undefined) return undefined;

  return {
    columnMenu: {
      hidden: {
        use: hidden?.hide !== true,
        title: text?.hide,
      },
    },
  };
};

const transformAutoFitOption = (gridOption?: GridOption): GridColumnAutoFitOption | undefined => {
  if (gridOption?.adjust === undefined) return undefined;
  return { use: gridOption.adjust };
};

const transformMenuItems = (menuItems?: MenuItem[]): ContextMenuItems[] | undefined => {
  if (!menuItems?.length) return undefined;

  return menuItems.map((item) => ({
    text: item.text != null ? String(item.text) : undefined,
    iconClass: item.iconClass as ContextMenuItems['iconClass'],
    itemClass: item.itemClass,
    disabled: item.disabled,
    hidden: item.hidden,
    children: item.children ? transformMenuItems(item.children) : undefined,
    click: item.click as ContextMenuItems['click'],
  }));
};

const hasExportExcelOption = (exportExcelOption?: ExportExcelOption): boolean => {
  if (!exportExcelOption) return false;
  return !!(
    exportExcelOption.title ||
    exportExcelOption.rowDataFormatter ||
    exportExcelOption.exportByServer ||
    exportExcelOption.rowDataFetchPromise
  );
};

const transformGridSettingOption = (
  gridOption?: GridOption,
  exportExcelOption?: ExportExcelOption,
): GridSettingOption | undefined => {
  const useGridSetting = gridOption?.useGridSetting;
  const useExportExcel = gridOption?.useExportExcel;

  if (!useGridSetting?.use) return { use: false };

  const hasExportExcel =
    useExportExcel?.use === false
      ? false
      : useExportExcel?.use || hasExportExcelOption(exportExcelOption);

  const result: GridSettingOption = { use: true };

  result.useExportExcel = hasExportExcel
    ? { use: true, title: useExportExcel?.title, icon: useExportExcel?.icon }
    : { use: false };

  if (hasExportExcel && exportExcelOption) {
    result.exportExcelOption = exportExcelOption;
  }

  result.useColumnVisibility = { use: useGridSetting.useDefaultColumnSetting ?? true };
  result.useResetUserEnv = { use: true };

  if (useGridSetting.useExpandCollapseMenu) {
    result.useExpandCollapseMenu = true;
  }

  if (useGridSetting.customContextMenu) {
    result.customMenu = transformMenuItems(useGridSetting.customContextMenu);
  }

  if (useGridSetting.onAfterResetUserEnv) {
    result.onAfterReset = useGridSetting.onAfterResetUserEnv;
  }

  return result;
};

const transformRowMenuOption = (gridOption?: GridOption): GridRowMenuOption | undefined => {
  if (!gridOption?.customContextMenu?.length) return undefined;
  return {
    use: true,
    menuItems: transformMenuItems(gridOption.customContextMenu) ?? [],
    customClass: gridOption.customContextMenuClass,
  };
};

const transformHeaderOption = (gridOption?: GridOption): GridStyleOption['header'] | undefined => {
  if (gridOption?.showHeader === undefined) return undefined;
  return { show: gridOption.showHeader };
};

const transformTreeGridExpandingOption = (
  gridOption?: GridOption,
): GridExpandingOption | undefined => {
  const treeOption = gridOption as
    | { expandIcon?: string; collapseIcon?: string; defaultExpandAll?: boolean }
    | undefined;

  const { expandIcon, collapseIcon, defaultExpandAll } = treeOption ?? {};

  if (!expandIcon && !collapseIcon && defaultExpandAll !== false) return undefined;

  // 아이콘 매핑: TreeGrid expandIcon → ModernGrid collapsedIcon (반대 의미)
  const result: GridExpandingOption = {};
  if (expandIcon) result.collapsedIcon = expandIcon as GridExpandingOption['collapsedIcon'];
  if (collapseIcon) result.expandedIcon = collapseIcon as GridExpandingOption['expandedIcon'];
  if (defaultExpandAll === false) result.expandAllOnLoad = false;

  return result;
};

const transformSummaryOption = (gridOption?: GridOption): GridSummaryOption | undefined => {
  if (!gridOption?.useSummary) return undefined;
  return { use: true };
};

export const transformGridOption = (
  gridOption?: GridOption,
  searchBarOption?: GridSearchBarOption & { useExternalSearchBar?: boolean },
  paginationInfo?: PaginationInfo,
  exportExcelOption?: ExportExcelOption,
  adapterOptions?: BaseGridAdapterOptions,
): GridOptionV2 => {
  const pagination = transformPaginationOption(gridOption, paginationInfo);
  const textFilter = transformTextFilterOption(searchBarOption);
  const tokenFilter = transformTokenFilterOption(searchBarOption);
  const sorting = transformSortingOption(gridOption);
  const rowSelection = transformRowSelectionOption(gridOption);
  const detailRow = transformDetailRowOption(gridOption);
  const autoFit = transformAutoFitOption(gridOption);
  const columnVisibility = transformColumnVisibilityOption(gridOption);
  const gridSetting = transformGridSettingOption(gridOption, exportExcelOption);
  const rowMenu = transformRowMenuOption(gridOption);
  const expanding = transformTreeGridExpandingOption(gridOption);
  const summary = transformSummaryOption(gridOption);

  const bodyStyle = transformStyleOption(gridOption);
  const headerStyle = transformHeaderOption(gridOption);
  const style = bodyStyle || headerStyle ? { ...bodyStyle, header: headerStyle } : undefined;

  return {
    ...(pagination && { pagination }),
    ...(textFilter && { textFilter }),
    ...(tokenFilter && { tokenFilter }),
    ...(sorting && { sorting }),
    ...(rowSelection && { rowSelection }),
    ...(detailRow && { detailRow }),
    ...(autoFit && { autoFit }),
    ...(columnVisibility && { columnVisibility }),
    ...(gridSetting && { gridSetting }),
    ...(style && { style }),
    ...(rowMenu && { rowMenu }),
    ...(expanding && { expanding }),
    ...(summary && { summary }),
    ...(gridOption?.count !== undefined && { useNumberColumn: gridOption.count }),
    ...(adapterOptions?.isTreeMode && { treeView: { use: true, subRowKey: 'children' } }),
  };
};
